import './style.scss';

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';

import { useLocation, useSearchParams } from 'react-router-dom';
import Pagination from 'components/Pagination';
import { getAllByFilter as getAllPrograms } from 'apis/programs.api';
import { Can } from 'components/Can';
import { handleEnterKeyPress, mapError } from 'utils/func';
import { TIMEZONE_VN } from 'constant';
import ConfirmDelete from './components/ConfirmDelete';
import Form from './components/Form';
import DeadlineTableDialog from './components/UpdateDeadline';
import ViewListDialog from './components/ViewListDialog';

export default function ProgramViewList() {
  // #region Data
  const { t } = useTranslation();
  const dialogFormRef = useRef(null);
  const dialogDeleteRef = useRef(null);
  const [isVisibleDeadline, setVisibleDeadline] = useState(false);
  const [isVisibleViewList, setIsVisibleViewList] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState({
    id: '',
    name: '',
  });
  const [searchParams] = useSearchParams({ replace: true });
  const location = useLocation();
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });

  const [filtersTable, setFiltersTable] = useState({
    _name: { value: null },
    _description: { value: null },
  });

  const [filters, setFilters] = useState({
    name: { value: null },
    description: { value: null },
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    name: { value: null },
    description: { value: null },
  });

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ['program', pagination, appliedFilters],
    ({ queryKey: [, _pagination, _appliedFilters] }) =>
      getAllPrograms(_pagination, _appliedFilters),
    {
      enabled: isLoadedFilterFromLocalStorage,
    }
  );

  const data = useMemo(() => _data?.data ?? [], [_data]);
  // #endregion

  // #region Event
  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ?? '{}'
    );

    setFilters({
      name: '',
      description: '',
      ...localFilters,
    });
    setAppliedFilters({
      name: '',
      description: '',
      ...localFilters,
    });

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );

    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }

    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10) || 0;
    setPagination((cur) => ({ ...cur, first: currentFirst }));
    setIsLoadedFilterFromLocalStorage(true);
  }, []);

  const handleClearFilter = (field) => {
    const newFilters = { ...filters, [field]: '' };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    localStorage.setItem(`admin-${location.pathname.slice(1)}-filters`, JSON.stringify(newFilters));
  };

  const handleApplyFilter = () => {
    setAppliedFilters(filters);
  };

  const handleOnChangeProgram = (value) => {
    setVisibleDeadline(true);
    setSelectedProgram({
      id: value?.id,
      name: value?.name,
    });
  };

  const hanldeOpenViewList = (value) => {
    setIsVisibleViewList(true);
    setSelectedProgram({
      id: value?.id,
      name: value?.name,
    });
  };

  // #endregion

  // eslint-disable-next-line react/prop-types
  const formatIndex = (value, props) => props.rowIndex + 1;

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN }) : '';

  const formatStartingTime = (value) => formatDate(value?.startingTime);
  const formatEndingTime = (value) => formatDate(value?.endingTime);
  const formatOrganizationPrequalificationDeadline = (value) =>
    formatDate(value?.organizationPrequalificationDeadline);
  const formatStaffPrequalificationDeadline = (value) =>
    formatDate(value?.staffPrequalificationDeadline);

  const handleKeyDown = useCallback(
    (e) => {
      handleEnterKeyPress(e, () => {
        setAppliedFilters(filters);
      });
    },
    [filters]
  );

  const textFilter = (field) => (
    <InputText
      value={filters[field]}
      onChange={(e) => {
        const newFilters = { ...filters, [field]: e.target.value };
        setFilters(newFilters);
      }}
      onKeyDown={handleKeyDown}
      placeholder={t(`program.${field}`)}
      className="p-column-filter"
    />
  );

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(filters)
        );
      }}
      severity="success"
    />
  );
  const filterActiveStyle = (field) =>
    filters?.[field]?.length > 0 || Object.values(filters[field]).some((e) => e)
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  const formatActions = (value) => (
    <div className="flex justify-content-around gap-1">
      <Can do="read" on="project_proposal">
        <Button
          severity="info"
          tooltip={t('program.viewProjectProposal.title')}
          tooltipOptions={{ position: 'left' }}
          icon="pi pi-eye"
          onClick={() => hanldeOpenViewList(value)}
        />
      </Can>
      <Can do="update" on="program">
        <Button
          severity="warning"
          tooltip={t('program.update.title')}
          tooltipOptions={{ position: 'left' }}
          icon="pi pi-pencil"
          onClick={() => {
            dialogFormRef.current.open({
              mode: 'UPDATE',
              value,
            });
          }}
        />
      </Can>
      <Can do="update" on="program">
        <Button
          severity="info"
          tooltip={t('program.deadline.title')}
          tooltipOptions={{ position: 'left' }}
          icon="pi pi-calendar-clock"
          onClick={() => handleOnChangeProgram(value)}
        />
      </Can>
      <Can do="delete" on="program">
        <Button
          severity="danger"
          tooltip={t('program.delete.title')}
          tooltipOptions={{ position: 'left' }}
          icon="pi pi-trash"
          onClick={() => {
            dialogDeleteRef.current.open(value);
          }}
        />
      </Can>
    </div>
  );

  return (
    <div className="card relative">
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex justify-content-between align-items-center mb-4 pb-2">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">{t('program.viewList.title')}</h4>
        <Can do="create" on="program">
          <Button
            className="bg-cyan-600 border-cyan-600"
            label={t('program.viewList.add')}
            icon="pi pi-plus"
            onClick={() => {
              dialogFormRef.current.open({
                mode: 'CREATE',
              });
            }}
          />
        </Can>
      </div>

      <DataTable
        value={data?.data}
        className="p-datatable-gridlines header-table"
        showGridlines
        scrollable
        dataKey="id"
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage={t('program.viewList.emptyMessage')}
        filters={filtersTable}
        onFilter={(e) => {
          setFiltersTable(e.filters);
        }}
        filterDisplay="menu"
      >
        <Column
          body={formatIndex}
          header={t('program.viewList.no')}
          className="text-center w-3rem"
          frozen
        />
        <Column
          field="name"
          header={t('program.name')}
          headerStyle={filterActiveStyle('name')}
          className="w-30rem"
          style={{ minWidth: '14rem' }}
          showFilterMatchModes={false}
          showFilterMenu
          filterField="_name"
          filter
          filterElement={() => textFilter('name')}
          filterApply={filterApplyTemplate}
          filterClear={filterClearTemplate}
          onFilterClear={() => handleClearFilter('name')}
          onFilterApplyClick={handleApplyFilter}
          sortable
          frozen
        />
        <Column
          filterField="_description"
          field="description"
          header={t('program.description')}
          headerStyle={filterActiveStyle('description')}
          showFilterMatchModes={false}
          showFilterMenu
          filter
          filterElement={() => textFilter('description')}
          filterApply={filterApplyTemplate}
          filterClear={filterClearTemplate}
          onFilterClear={() => handleClearFilter('description')}
          onFilterApplyClick={handleApplyFilter}
          style={{ minWidth: '14rem' }}
        />
        <Column
          header={t('program.template')}
          body={(value) => value.template}
          className="w-10rem text-center"
        />
        <Column
          header={t('program.startingTime')}
          body={formatStartingTime}
          className="w-10rem text-center"
        />
        <Column
          header={t('program.endingTime')}
          body={formatEndingTime}
          className="w-10rem text-center"
          field="endingTime"
          sortable
        />
        <Column
          header={t('program.organizationPrequalificationDeadline')}
          body={formatOrganizationPrequalificationDeadline}
          className="w-10rem text-center"
        />
        <Column
          header={t('program.staffPrequalificationDeadline')}
          body={formatStaffPrequalificationDeadline}
          className="w-10rem text-center"
        />

        <Column
          header={t('projectProposal.action')}
          body={formatActions}
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data?.pagination}
        pageName="programs"
      />

      <Form ref={dialogFormRef} refetch={refetch} />
      <DeadlineTableDialog
        visible={isVisibleDeadline}
        setVisible={setVisibleDeadline}
        program={selectedProgram}
      />

      <ViewListDialog
        visible={isVisibleViewList}
        setVisible={setIsVisibleViewList}
        programId={selectedProgram?.id}
      />

      <Can do="delete" on="program">
        <ConfirmDelete ref={dialogDeleteRef} refetch={refetch} />
      </Can>
    </div>
  );
}
